import React, { useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { syncUserDataFromBackend } from '../../services/dataSyncService';
import { claimNilOwnerTripsForUser } from '../../services/tripsService';

/**
 * When the user becomes authenticated, refresh subscription + pull backend data
 * into localStorage so they can continue where they left off.
 * Renders nothing.
 *
 * Re-runs when the authenticated user changes (sub).
 * Does not wipe other users' local trip caches (per-user isolation by storage key).
 */
const DataSyncOnAuth: React.FC = () => {
  const { user, isLoading, setDataSyncing } = useAuth();
  const { refresh: refreshSubscription } = useSubscription();
  const syncedForSubRef = useRef<string | null>(null);

  useEffect(() => {
    if (isLoading || !user?.sub || !user?.email) return;
    if (syncedForSubRef.current === user.sub) return;
    syncedForSubRef.current = user.sub;
    setDataSyncing(true);

    (async () => {
      try {
        // Claim nil-owner trips for this sub before pull (never re-home foreign owners).
        claimNilOwnerTripsForUser(user.sub);
        await refreshSubscription();
        await syncUserDataFromBackend(user.sub, user.email);
      } finally {
        setDataSyncing(false);
      }
    })();
  }, [isLoading, user?.sub, user?.email, setDataSyncing, refreshSubscription]);

  return null;
};

export default DataSyncOnAuth;
